/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { DrmType } from 'client-code/video-provider-configurations';

import { TokenRefreshMechansim } from '../../device/TokenRefreshMechanism';
import { ServicesClientImpl } from '../../interactors';
import { Log } from '../../logging';
import { Storage } from '../../storage/Storage';
import { ITivoSubscription, TivoSubscriptionModel } from '../../tivo';
import { SDTState, VideoProviderState } from '../enums/index';
import { IDrmManager, IVpcDrmInfo } from '../interfaces';
import { TScopedDomainTokenNotifier } from '../types';
import { ScopedDomainTokenModel } from './ScopedDomianTokenModel';

export class DrmManager implements IDrmManager {
    private static oDrmManager: DrmManager | null = null;

    private mSDTModel: ScopedDomainTokenModel | null = null;

    private drmInfo: IVpcDrmInfo = {} as IVpcDrmInfo;

    private oITivoSubscription: ITivoSubscription<VideoProviderState>;

    private services = new ServicesClientImpl();

    private drmErrorCount: number = 0;

    private readonly TAG: string = 'DrmManager';

    private constructor() {
        this.setupSDT();
        this.oITivoSubscription = new TivoSubscriptionModel<VideoProviderState>();
    }

    private setupSDT() {
        this.mSDTModel = ScopedDomainTokenModel.getInstance();
        this.mSDTModel.getSDTFetchListener().subscribe(this.onTokenFetch.bind(this));
    }

    // eslint-disable-next-line class-methods-use-this
    private log(
        level: 'debug' | 'info' | 'error',
        message: string,
        ...optionalParams: any[]
    ): void {
        Diagnostics[level](this.TAG, message, ...optionalParams);
        Log[level[0]](this.TAG, message, ...optionalParams);
        if (level === 'error') Diagnostics.error(this.TAG, 'stacktrace:,', optionalParams[0]);
    }

    private onTokenFetch(sdtNotifyObject: TScopedDomainTokenNotifier) {
        if (sdtNotifyObject.drmCustomDomain !== this.drmInfo?.drmCustomDomain) {
            return;
        }
        if (
            sdtNotifyObject.sdtState === SDTState.SUCCESS ||
            sdtNotifyObject.sdtState === SDTState.REFRESH
        ) {
            this.drmInfo.token = sdtNotifyObject?.token ?? '';
            this.oITivoSubscription.notify(
                sdtNotifyObject.sdtState === SDTState.REFRESH
                    ? VideoProviderState.REFRESH
                    : VideoProviderState.SUCCESS,
            );
            this.setupScheduleSDTReferesh();
            this.drmErrorCount = 0;
        } else if (sdtNotifyObject.sdtState === SDTState.ERROR) {
            if (
                !this.drmInfo.token ||
                sdtNotifyObject.drmCustomDomain === this.drmInfo.drmCustomDomain
            ) {
                this.drmErrorCount += 1;
                this.oITivoSubscription.notify(VideoProviderState.ERROR);
            }
        }
    }

    private async isDrmInfoMissing(drmConfig): Promise<boolean> {
        if (
            !drmConfig.drmServerType ||
            drmConfig.drmServerType === '' ||
            !drmConfig.drmServerUrl ||
            drmConfig.drmServerUrl === '' ||
            !drmConfig.drmCustomHeader ||
            drmConfig.drmCustomHeader === '' ||
            !drmConfig.transportEncodingType ||
            drmConfig.transportEncodingType === ''
        ) {
            this.drmErrorCount += 1;
            this.oITivoSubscription.notify(VideoProviderState.ERROR);
            return true;
        }
        return false;
    }

    public static getInstance(): DrmManager {
        if (!DrmManager.oDrmManager) {
            DrmManager.oDrmManager = new DrmManager();
        }

        return DrmManager.oDrmManager;
    }

    public getDrmManagerListener(): ITivoSubscription<VideoProviderState> {
        return this.oITivoSubscription;
    }

    public fetchVideoProviderConfiguration() {
        this.services
            .loadVpConfigurations()
            .then(async () => {
                const vpConfigurationsData = Storage.getInstance().getItem(
                    Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
                );

                const vpcDataArray = Object.values(vpConfigurationsData) as any[];

                // Process configurations sequentially to avoid race conditions
                const processVpcData = async (vpcData: any): Promise<void> => {
                    try {
                        if (vpcData.drmConfig.drmServerType === DrmType.MobiWidevine) {
                            await this.mSDTModel?.getToken(
                                vpcData.drmConfig.drmCustomDomain,
                                vpcData.drmConfig.drmCustomHeaderAuthScheme,
                            );
                        }
                    } catch {
                        // On api error ScopeDomainTokenModel will notify
                        // error as SDTState which will trigger to retry mechanism accordingly
                    }
                };

                // Use reduce to process configurations sequentially
                await vpcDataArray.reduce(async (previousPromise, vpcData) => {
                    await previousPromise;
                    return processVpcData(vpcData);
                }, Promise.resolve());
            })
            .catch((error) => {
                this.log('error', 'Failed to load video provider configurations.', error);
                Log.e(this.TAG, 'Failed to load video provider configurations.', error);
            });
    }

    public async getDrmInfoFroProviderID(
        providerId: string,
        isForced?: boolean,
    ): Promise<IVpcDrmInfo> {
        this.drmInfo = {} as IVpcDrmInfo;
        let vpcObject;
        try {
            let response = await this.services.fetchVPConfigurationsForVideoPlayer(providerId);
            vpcObject = response.find((obj: any) => obj.providerPartnerId === providerId);

            if (
                !vpcObject ||
                (vpcObject?.drmConfig?.drmServerType === DrmType.Widevine && isForced)
            ) {
                response = await this.services.fetchVPConfigurationsForVideoPlayer(
                    providerId,
                    true,
                );
            }
        } catch {
            this.log(
                'error',
                `fetchVPConfigurationsForVideoPlayer failed for providerId: ${providerId}`,
            );
            this.drmErrorCount += 1;
            this.oITivoSubscription.notify(VideoProviderState.ERROR);
        }

        let isDrmInfoMissig = false;
        if (vpcObject) isDrmInfoMissig = await this.isDrmInfoMissing(vpcObject?.drmConfig);

        if (vpcObject && !isDrmInfoMissig) {
            this.drmInfo.drmType = vpcObject.drmConfig.drmServerType || 'clear';
            this.drmInfo.drmServerUrl = vpcObject.drmConfig.drmServerUrl || '';
            this.drmInfo.drmCustomHeader = vpcObject.drmConfig.drmCustomHeader || '';
            this.drmInfo.transportEncodingType = vpcObject.drmConfig.transportEncodingType
                ? vpcObject.drmConfig.transportEncodingType[0].toString()
                : '';
            this.drmInfo.providerPartnerId = vpcObject.providerPartnerId || '';

            if (vpcObject.drmConfig.drmServerType === DrmType.MobiWidevine) {
                this.drmInfo.drmCustomDomain = vpcObject?.drmConfig?.drmCustomDomain || '';
                this.drmInfo.drmCustomHeaderAuthScheme =
                    vpcObject?.drmConfig?.drmCustomHeaderAuthScheme || '';
                const mobiDrmToken = await this.mSDTModel?.getToken(
                    vpcObject?.drmConfig?.drmCustomDomain || '',
                    vpcObject?.drmConfig?.drmCustomHeaderAuthScheme || '',
                    isForced,
                );
                this.drmInfo.token = mobiDrmToken?.token || '';
            } else {
                this.drmInfo.token = vpcObject.drmConfig.serverSuppliedDeviceIdentifier || '';
            }
        }
        return this.drmInfo;
    }

    public setupScheduleSDTReferesh(): void {
        const tokenWithlowestExpirationTime = this.mSDTModel?.getTokenToBeRefreshed();
        if (tokenWithlowestExpirationTime) {
            TokenRefreshMechansim.getInstance().scheduleDomainTokenRefresh(
                tokenWithlowestExpirationTime,
            );
        }
    }

    public getDRMConfig(): IVpcDrmInfo {
        return this.drmInfo;
    }

    public getDRMErrorCount(): Promise<number> {
        return Promise.resolve(this.drmErrorCount);
    }
}
